import { useEffect, useState } from 'react';
import { api, logout } from '@/lib/api';
import { stashChargerDeepLink } from '@/lib/pendingDeepLink';
import { useAuthStore } from '@/store/auth';
import { canonicalHostProgramUrl } from '@evi/shared';

const endpoint = 'host-campaign/aaron';

// Keep identity verification on EVI's existing sign-in surface. The private form,
// mailbox proof, and return from Stripe belong to Admin. No login token goes in a URL.
export function HostCampaignSignIn({ signedIn, onNavigate }: { signedIn: boolean; onNavigate: (path: string) => void }) {
  const email = useAuthStore(s => s.user?.email);
  const [admin, setAdmin] = useState(''), [error, setError] = useState(''), [working, setWorking] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    const canonical = canonicalHostProgramUrl(window.location);
    if (canonical) window.history.replaceState(window.history.state, '', canonical);
    void api.get(endpoint + '/entry').json<{ data: { admin_url: string } }>().then(({ data }) => {
      if (!active) return;
      const proof = new URLSearchParams(window.location.hash.slice(1)).get('verify');
      if (proof && /^[\w-]{43}$/.test(proof)) {
        window.location.replace(data.admin_url + '#verify=' + proof);
        return;
      }
      const mode = new URLSearchParams(window.location.search).get('auth');
      if (mode === 'retry') {
        window.location.replace(data.admin_url);
        return;
      }
      if (!signedIn && (mode === 'register' || mode === 'login')) {
        stashChargerDeepLink();
        onNavigate(mode === 'register' ? '/register' : '/login');
      } else if (!signedIn) window.location.replace(data.admin_url);
      else setAdmin(data.admin_url);
    }).catch(() => { if (active) setError('Could not open your private application. Please try again.'); });
    return () => { active = false; };
  }, [signedIn, onNavigate, attempt]);
  async function proceed() {
    if (!admin) { setError(''); setAttempt(n => n + 1); return; }
    setWorking(true); setError('');
    try { await api.post(endpoint + '/portal-session', { retry: 0 }); window.location.replace(admin); }
    catch { setError('Could not continue to EVI Admin. Please try again.'); setWorking(false); }
  }
  async function changeAccount() {
    setWorking(true); setError('');
    try {
      await logout(); stashChargerDeepLink(); useAuthStore.getState().clearAuth(); onNavigate('/login');
    } catch { setError('Could not sign out. Please try again.'); setWorking(false); }
  }
  return <main style={{ maxWidth: '36rem', margin: '3rem auto', padding: '1rem' }}>
    <h1>Continue to your site application</h1>
    {admin && email && <p>Signed in as {email}. Your site application is in EVI Admin.</p>}
    {(admin || error) && <button disabled={working} onClick={() => void proceed()}>{admin ? 'Continue to EVI Admin' : 'Try again'}</button>}
    {admin && email && <button disabled={working} onClick={() => void changeAccount()}>Use a different account</button>}
    {!admin && !error && <p>Opening EV Initiative sign-in…</p>}
    {error && <p role="alert">{error}</p>}
  </main>;
}
